import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { CheckboxRaw } from '@/shared/ui/components/form/inputs/Checkbox';

interface Props {
  renewalDate: string;
  /** The tier below, when there is one and no downgrade is already scheduled. */
  downgradeTo: { key: string; title: string; priceLabel: string } | null;
  /** Only offered while the subscription is billed monthly. */
  yearlyDiscountPercent: number | null;
  checks: boolean[];
  onToggleCheck: (index: number) => void;
  onPickDowngrade: (key: string) => void;
  onPickYearly: () => void;
  /** Omitted where the screen has nowhere to open the conversation. */
  onTalkToUs?: () => void;
}

/**
 * Cancellation, with the ways out offered before the door.
 *
 * The alternatives are not decoration: price and a missing feature are the
 * two reasons people cancel a plan they otherwise use, and both have an
 * answer that is not leaving. Each one switches this same modal to the plan
 * flow instead of opening another dialog.
 *
 * @param props - Component props.
 * @returns The cancellation body.
 */
export function PlanFlowCancel(props: Props) {
  const { t } = useTranslation('features/pricing');
  const {
    renewalDate, downgradeTo, yearlyDiscountPercent, checks, onToggleCheck,
    onPickDowngrade, onPickYearly, onTalkToUs,
  } = props;

  const checkKeys = ['check1', 'check2', 'check3'] as const;

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-(--space-5)">
      <section className="flex flex-col gap-(--space-3)">
        <Text component="span" className="[font-weight:var(--font-semibold)]">
          {t('planFlow.cancel.beforeTitle')}
        </Text>
        <Text component="p" className="text-(length:--text-body-sm) text-(--text-secondary)">
          {t('planFlow.cancel.beforeBody')}
        </Text>

        {downgradeTo && (
          <ExitCard
            title={t('planFlow.cancel.downgradeTo', { plan: downgradeTo.title })}
            note={downgradeTo.priceLabel}
            cta={t('planFlow.cancel.downgradeCta', { plan: downgradeTo.title })}
            testId="plan-flow-exit-downgrade"
            onClick={() => onPickDowngrade(downgradeTo.key)}
          />
        )}

        {yearlyDiscountPercent !== null && (
          <ExitCard
            title={t('planFlow.cancel.yearly', { percent: yearlyDiscountPercent })}
            cta={t('planFlow.cancel.yearlyCta')}
            testId="plan-flow-exit-yearly"
            onClick={onPickYearly}
          />
        )}

        {onTalkToUs && (
          <ExitCard
            title={t('planFlow.cancel.talk')}
            cta={t('planFlow.cancel.talkCta')}
            testId="plan-flow-exit-talk"
            onClick={onTalkToUs}
          />
        )}
      </section>

      <section className="flex flex-col gap-(--space-3)">
        <Text component="span" className="[font-weight:var(--font-semibold)]">
          {t('planFlow.cancel.whatHappens')}
        </Text>

        <div className="flex flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) p-(--space-4)">
          <div className="grid grid-cols-[92px_1fr] gap-(--space-3)">
            <Text component="span" className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)]">
              {t('planFlow.cancel.today')}
            </Text>
            <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
              {t('planFlow.cancel.todayBody')}
            </Text>
          </div>
          <div className="grid grid-cols-[92px_1fr] gap-(--space-3)">
            <Text component="span" className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)]">
              {renewalDate}
            </Text>
            <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
              {t('planFlow.cancel.endBody')}
            </Text>
          </div>
        </div>

        <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
          {t('planFlow.cancel.readCarefully')}
        </Text>

        <div className="flex flex-col gap-(--space-2)">
          {checkKeys.map((key, index) => (
            <label
              key={key}
              className="flex cursor-pointer items-start gap-(--space-2) text-(length:--text-body-sm)"
            >
              <CheckboxRaw
                value={checks[index] ?? false}
                onToggle={() => onToggleCheck(index)}
                data-testid={`plan-flow-cancel-check-${index}`}
              />
              <span>{t(`planFlow.cancel.${key}`, { date: renewalDate })}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

/** One way out: what it is, and the button that takes it. */
function ExitCard(props: {
  title: string;
  note?: string;
  cta: string;
  testId: string;
  onClick: () => void;
}) {
  const { title, note, cta, testId, onClick } = props;
  return (
    <div
      className={cn(
        'flex items-center gap-(--space-3) rounded-(--radius-md)',
        'border border-(--border-subtle) p-(--space-3)',
      )}
    >
      <div className="flex min-w-0 flex-col">
        <Text component="span" className="text-(length:--text-body-sm)">{title}</Text>
        {note && (
          <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
            {note}
          </Text>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={onClick} data-testid={testId} className="ms-auto shrink-0">
        {cta}
      </Button>
    </div>
  );
}

export default PlanFlowCancel;
